import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Input from '../ui/Input';
import RequiredDocumentsPanel from './RequiredDocumentsPanel';
import RejectDialog from './RejectDialog';
import DynamicField from '../fields/DynamicField';
import { processCandidateStep } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';
import { usePermission } from '../../auth/usePermission';
import { formatValidationErrors } from '../../utils/errors';

const META_FIELD = {
  swipe_card: { key: 'employee_no', label: 'Employee No', required: true },
  permit_issuance: { key: 'permit_no', label: 'Permit No', required: false },
};

function actionsFor(step) {
  if (step.kind !== 'decision') return [null, null];
  const positive = step.decision_labels?.positive === 'Approved' ? 'approve' : 'pass';
  const negative = step.decision_labels?.negative === 'Rejected' ? 'reject' : 'fail';
  return [positive, negative];
}

export default function CurrentStepPanel({ candidate, step }) {
  const queryClient = useQueryClient();
  const { can } = usePermission();

  // allowed_actions from the candidate is only computed for the literal current step; a step
  // being caught up out of order needs the same permission + document check done locally.
  const permissionName = `step.process.${step.code}`;
  const allowed = step.is_current
    ? candidate.allowed_actions
    : {
        can_process: can(permissionName) && (step.missing_documents?.length ?? 0) === 0,
        missing_permission: can(permissionName) ? null : permissionName,
        missing_documents: step.missing_documents || [],
      };

  const [remarks, setRemarks] = useState(step.remarks || '');
  const [checklistState, setChecklistState] = useState(step.checklist_state || {});
  const [answers, setAnswers] = useState(step.answers || {});
  const [metaValue, setMetaValue] = useState(step.meta?.[META_FIELD[step.code]?.key] || '');
  const [rejectOpen, setRejectOpen] = useState(false);

  const metaField = META_FIELD[step.code];

  const mutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step updated.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      setRejectOpen(false);
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not process step.';
      pushToast({ type: 'error', message });
    },
  });

  const buildPayload = (action, overrideRemarks) => ({
    action,
    remarks: overrideRemarks ?? remarks,
    checklist_state: checklistState,
    answers,
    meta: metaField && metaValue ? { [metaField.key]: metaValue } : undefined,
  });

  const handlePositive = (action) => mutation.mutate(buildPayload(action));
  const handleReject = (action, reason) => mutation.mutate(buildPayload(action, reason));

  const toggleChecklistItem = (item) => {
    setChecklistState((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));

  const [positiveAction, negativeAction] = actionsFor(step);
  const isReprocessableFail = step.status === 'failed' && step.negative_outcome === 'company_policy';

  return (
    <div className="rounded-lg border border-primary-light bg-primary-50/40 p-4">
      {step.description && <p className="mb-3 text-sm text-gray-600">{step.description}</p>}

      {isReprocessableFail && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Previous attempt failed{step.meta?.attempts?.length ? ` (${step.meta.attempts.length} attempt(s))` : ''} —
          you can retry.
        </p>
      )}

      {step.checklist?.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Checklist</h4>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            {step.checklist.map((item) => (
              <label key={item} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="accent-primary" checked={!!checklistState[item]} onChange={() => toggleChecklistItem(item)} />
                {item}
              </label>
            ))}
          </div>
        </div>
      )}

      {step.fields?.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Fields</h4>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
            {step.fields.map((f) => (
              <div key={f.id}>
                <label className="mb-1 block text-sm text-gray-700">
                  {f.label}
                  {f.validation?.required && <span className="text-red-500"> *</span>}
                </label>
                <DynamicField field={f} value={answers[f.id]} onChange={(value) => setAnswer(f.id, value)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {step.required_documents?.length > 0 && (
        <div className="mb-4">
          <RequiredDocumentsPanel
            requiredDocuments={step.required_documents}
            documents={candidate.documents}
            candidateId={candidate.id}
            candidateStepId={step.candidate_step_id}
          />
        </div>
      )}

      {metaField && (
        <Input
          label={metaField.label}
          required={metaField.required}
          value={metaValue}
          onChange={(e) => setMetaValue(e.target.value)}
          className="mb-4"
        />
      )}

      <Textarea label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mb-4" />

      {allowed.can_process ? (
        <div className="flex flex-wrap gap-3">
          {step.kind === 'task' ? (
            <Button onClick={() => handlePositive('complete')} loading={mutation.isPending}>
              Mark Complete
            </Button>
          ) : (
            <>
              <Button onClick={() => handlePositive(positiveAction)} loading={mutation.isPending} className="capitalize">
                {positiveAction}
              </Button>
              <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={mutation.isPending} className="capitalize">
                {negativeAction}
              </Button>
            </>
          )}
        </div>
      ) : (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
          {allowed.missing_permission
            ? `Requires permission: ${allowed.missing_permission}`
            : allowed.missing_documents?.length > 0
              ? `Missing verified documents: ${allowed.missing_documents.join(', ')}`
              : 'You cannot process this step.'}
        </p>
      )}

      {step.kind === 'decision' && (
        <RejectDialog
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          onConfirm={(reason) => handleReject(negativeAction, reason)}
          loading={mutation.isPending}
          actionLabel={negativeAction === 'reject' ? 'Reject' : 'Fail'}
          closesRecruitment={step.negative_outcome === 'reject_candidate'}
        />
      )}
    </div>
  );
}
